import { _ } from "../../core/polyglot/polyglot.js";
import { fill } from "@foretagsplatsen/core-helpers/array";
import {
	makeComputationApiRepository,
	makeMonitorRepository,
	makeReferentialApiRepository,
} from "../../core/repositories/repositoryBuilders.js";
import AccountingDocumentService from "../shared/documentInfrastructure/AccountingDocumentService.js";
import AgencyUser from "../../models/AgencyUser.js";
import Budget from "../../models/Budget.js";
import ChartOfAccounts from "../../models/chartOfAccounts/ChartOfAccounts.js";
import CompanyUser from "../../models/CompanyUser.js";
import CurrentPeriodForecastColumnTemplate from "../../models/forecast/template/CurrentPeriodForecastColumnTemplate.js";
import Forecast from "../../models/forecast/instance/Forecast.js";
import ForecastBalanceSheet from "../../models/forecast/interpretation/reports/ForecastBalanceSheet.js";
import ForecastCashFlow from "../../models/forecast/interpretation/reports/ForecastCashFlow.js";
import ForecastComputationResult from "../../models/forecast/instance/ForecastComputationResult.js";
import ForecastIncomeStatement from "../../models/forecast/interpretation/reports/ForecastIncomeStatement.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import InternationalizationService from "../../core/services/InternationalizationService.js";
import InterpretationContext from "../../models/reports/InterpretationContext.js";
import ListSource from "../../core/list/ListSource.js";
import ODataStream from "../../core/list/streams/ODataStream.js";
import PredictedForecastColumnTemplate from "../../models/forecast/template/PredictedForecastColumnTemplate.js";
import ReferenceForecastColumnTemplate from "../../models/forecast/template/ReferenceForecastColumnTemplate.js";
import RelativePeriodTemplate from "../../models/reports/templates/RelativePeriodTemplate.js";
import ReportManager from "../../models/forecast/instance/ReportManager.js";
import ReportTemplate from "../../models/reports/templates/ReportTemplate.js";
import ReportTemplateForForecastingService from "../../core/services/ReportTemplateForForecastingService.js";
import RuleTemplate from "../../models/forecast/template/RuleTemplate.js";
import SeriesSpecificationBuilder from "../../models/forecast/instance/SeriesSpecificationBuilder.js";
import TemplateInterpreter from "../../models/reports/TemplateInterpreter.js";
import VatSettings from "../../models/VatSettings.js";

/**
 * Service responsible for all data exchange required by the forecast
 * viewer document.  This service coordinates other services and repositories.
 *
 * @param {session} session - The user's session. Used to get the
 * current company, current period…
 */
export default class ForecastViewerDocumentService extends AccountingDocumentService {
	constructor({ session } = {}) {
		super(...arguments);

		this._company = session.getCompany();
		this._currentUser = session.getCurrentUser();

		this._chartOfAccountsRepository = makeComputationApiRepository({
			route: "Forecasts/#forecastId/ChartOfAccounts",
			model: ChartOfAccounts,
			session,
		});

		this._keyFigureService = getKeyFigureService();

		this._computationRepository = makeComputationApiRepository({
			route: "Forecasts/#id/Series",
			model: ForecastComputationResult,
			session,
		});

		this._forecastRepository = makeComputationApiRepository({
			model: Forecast,
			route: "Forecasts/?id",
			session,
		});

		this._vatSettingsRepository = makeReferentialApiRepository({
			route: "Settings/VAT",
			session,
			model: VatSettings,
		});

		const reportTemplateRepository = makeComputationApiRepository({
			route: "Forecasts/#forecastId/ReportTemplates/?id",
			session,
			// Since a forecast doesn't necessarily contain all reports, a
			// non-existing report template shouldn't raise an error.
			responseHandlers: [{ test: 404, handle: () => null }],
		});

		this._reportTemplateService = new ReportTemplateForForecastingService({
			reportTemplateRepository,
		});

		this._scenarioRuleTemplateRepository = makeComputationApiRepository({
			route: "Forecasts/#forecastId/Scenarios/Baseline/RuleTemplates",
			model: RuleTemplate,
			session,
		});

		const antiForgeryToken = session.getAntiForgeryToken();

		this._notificationRepository = makeMonitorRepository({
			route: "/Accounting2/company/#companyId/ForecastNotification",
			defaultFilters: { companyId: this._company.getLegacyId() },
			antiForgeryToken,
		});

		this._companyUsersRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/User",
			model: CompanyUser,
			defaultFilters: { companyId: this._company.getLegacyId() },
			antiForgeryToken,
		});

		this._companyAgencyUsersRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/AgencyUser",
			model: AgencyUser,
			defaultFilters: { companyId: this._company.getLegacyId() },
			antiForgeryToken,
		});

		this._forecastToNewBudgetRepository = makeMonitorRepository({
			model: Budget,
			route: "/Accounting2/Company/#companyId/Forecast/#forecastId/Budget",
			defaultFilters: { companyId: this._company.getLegacyId() },
			antiForgeryToken,
		});
	}

	/**
	 * Request the initial data required by the document.
	 */
	async fetchInitialData({ forecastId }) {
		await super.fetchInitialData(...arguments);

		const [forecast, vatSettings, companyUsers, agencyUsers] =
			await Promise.all([
				this._getForecast(forecastId),
				this._vatSettingsRepository.get(),
				this._companyUsersRepository.get(),
				this._companyAgencyUsersRepository.get(),
			]);

		await this._setRuleTemplatesIntoForecast({ forecast, vatSettings });

		return {
			forecast,
			vatSettings,
			company: this._company,
			currentUser: this._currentUser,
			companyUsers,
			agencyUsers,
		};
	}

	getRuleTemplateSource({ forecastId }) {
		return ListSource.makeAndFetch({
			stream: new ODataStream({
				fetcher: ({ filter }) =>
					this._scenarioRuleTemplateRepository.get({
						filter: { ...filter, forecastId },
					}),
			}),
		});
	}

	sendNotifications({ forecastId, emailContent }) {
		return this._notificationRepository.save({
			model: { forecastId, ...emailContent.serialize() },
			successMessage: _("Mail sent!"),
			errorMessage: _("Failed to send the notification!"),
		});
	}

	saveForecastToNewBudget({ forecastId, budgetName, start, end }) {
		return this._forecastToNewBudgetRepository.save({
			model: { budgetName, start, end },
			filter: { forecastId },
		});
	}

	async _getForecast(forecastId) {
		const forecast = await this._forecastRepository.get({
			filter: { id: forecastId },
		});

		await this._addReportsToForecast({ forecast });
		await this._computeForecast({ forecast });

		return forecast;
	}

	async _setRuleTemplatesIntoForecast({ forecast, vatSettings }) {
		this._scenarioRuleTemplateRepository.addToContext({
			vatRates: vatSettings.getVatRates(),
		});

		const ruleTemplates = await this._scenarioRuleTemplateRepository.get({
			filter: {
				forecastId: forecast.getId(),
			},
		});

		forecast.getBaselineScenario().setRuleTemplates(ruleTemplates);
	}

	async _addReportsToForecast({ forecast }) {
		const forecastId = forecast.getId();

		const [reportTemplates, chartOfAccounts, keyFigures] =
			await Promise.all([
				this._getReportTemplates({ forecastId }),
				this._chartOfAccountsRepository.get({ filter: { forecastId } }),
				this._keyFigureService.getAllFromCache(),
			]);

		this._computationRepository.addToContext({ keyFigures });

		await this._ensureChartOfAccountsLanguage(chartOfAccounts);

		const reports = this._interpretReportTemplates({
			forecast,
			chartOfAccounts,
			keyFigures,
			reportTemplates,
		});

		forecast.setReportManager(new ReportManager(reports));
	}

	async _getReportTemplates({ forecastId }) {
		const reportTemplatesData =
			await this._reportTemplateService.getReportTemplates({
				filter: { forecastId },
			});

		const [incomeStatement, balanceSheet, cashFlowReport] =
			reportTemplatesData.map((element) =>
				ReportTemplate.materialize(element),
			);

		return {
			incomeStatement,
			balanceSheet,
			cashFlowReport,
		};
	}

	async _computeForecast({ forecast }) {
		const builder = new SeriesSpecificationBuilder({
			keyFigures: this._computationRepository.getContext().keyFigures,
		});

		const computationResult = await this._computationRepository.save({
			notify: false,
			model: forecast
				.makeComputationSeriesSpecifications({ builder })
				.map((specification) => specification.getKey()),
			filter: { id: forecast.getId() },
		});

		forecast.setDataFromSeries(computationResult);
	}

	_interpretReportTemplates({
		forecast,
		chartOfAccounts,
		keyFigures,
		reportTemplates,
	}) {
		const period = forecast.getCurrentPeriod().asPeriod();

		const interpretationContext = new InterpretationContext({
			chartOfAccounts,
			period,
			company: this._company,
			keyFigures,
		});

		const incomeStatement = this._interpretReportTemplate({
			reportTemplate: reportTemplates.incomeStatement,
			forecast,
			interpretationContext,
			ForecastReportClass: ForecastIncomeStatement,
		});

		const balanceSheet = this._interpretReportTemplate({
			reportTemplate: reportTemplates.balanceSheet,
			forecast,
			interpretationContext,
			ForecastReportClass: ForecastBalanceSheet,
		});

		const cashFlow = this._interpretReportTemplate({
			reportTemplate: reportTemplates.cashFlowReport,
			forecast,
			interpretationContext,
			ForecastReportClass: ForecastCashFlow,
		});

		return { incomeStatement, balanceSheet, cashFlow };
	}

	_interpretReportTemplate({
		reportTemplate,
		forecast,
		interpretationContext,
		ForecastReportClass,
	}) {
		if (!reportTemplate) return null;

		reportTemplate.setColumns([
			...this._makeReferenceForecastColumnTemplates({ forecast }),
			this._makeCurrentForecastColumnTemplate({ forecast }),
			...this._makePredictedForecastColumnTemplates({ forecast }),
		]);

		const report = new TemplateInterpreter({
			context: interpretationContext,
		}).interpret(reportTemplate);

		report.expandRowsUpToDefaultLevel();

		return new ForecastReportClass({ report });
	}

	_makeReferenceForecastColumnTemplates({ forecast }) {
		// Remove last period which is a current period duplication
		const length = forecast.getNumberOfReferencePeriods() - 1;

		return fill({
			length,
			fn: (index) =>
				new ReferenceForecastColumnTemplate({
					periodTemplate: new RelativePeriodTemplate({
						periodType: "Month",
						shiftPeriodType: "Month",
						shift: index - length,
					}),
				}),
		});
	}

	_makeCurrentForecastColumnTemplate() {
		return new CurrentPeriodForecastColumnTemplate({
			periodTemplate: new RelativePeriodTemplate({
				periodType: "Month",
				shiftPeriodType: "Month",
				shift: 0,
			}),
		});
	}

	_makePredictedForecastColumnTemplates({ forecast }) {
		return fill({
			length: forecast.getNumberOfPredictedPeriods(),
			fn: (index) =>
				new PredictedForecastColumnTemplate({
					periodTemplate: new RelativePeriodTemplate({
						periodType: "Month",
						shiftPeriodType: "Month",
						shift: index + 1,
					}),
				}),
		});
	}

	_ensureChartOfAccountsLanguage(chartOfAccounts) {
		const chartOfAccountsLanguage = chartOfAccounts.getLanguage();

		if (!chartOfAccountsLanguage) {
			return Promise.resolve();
		}

		return InternationalizationService.instance().ensureTranslations(
			chartOfAccountsLanguage,
		);
	}
}
